import { useState } from 'react';
import { createClientWithPin } from './supabase';
import { C, F, Card, Btn } from './tokens';

// Display-only title-casing for names interpolated into greeting messages.
// Same behavior as the helper in Portal.js — kept local to avoid a circular
// import; lift to a shared util if a third caller appears.
function titleCaseName(s){
  if (typeof s !== "string") return s;
  return s.replace(/\b([a-z])/g, (_, c) => c.toUpperCase());
}

// Shared Add-Client form. Owns its own form/validation state and post-create
// invite-URL + message banner. The parent controls visibility (mount/unmount)
// and supplies the advisorId to assign on the new clients row.
//
// advisorId: user.id to set as advisor_id on the new client. Master passes
//   its own id; an advisor will pass their own.
// onCreated: optional callback fired after a successful insert. Parents use
//   this to refresh their client list.
export default function AddClientForm({ advisorId, onCreated }) {
  const [newClient,setNewClient]=useState({firstName:"",lastName:"",company:"",email:"",pin:"",hiddenTabs:[]});
  const [addingClient,setAddingClient]=useState(false);
  const [inviteResult,setInviteResult]=useState(null); // {url, message}
  const [copied,setCopied]=useState(false);
  const [copiedMessage,setCopiedMessage]=useState(false);

  async function handleAddClient(){
    if(!newClient.firstName||!newClient.pin||newClient.pin.length!==5) return;
    setAddingClient(true);
    try{
      const cl=await createClientWithPin(advisorId,{
        firstName:newClient.firstName,lastName:newClient.lastName,
        company:newClient.company||`${newClient.firstName} ${newClient.lastName}`,
        email:newClient.email,
        pin:newClient.pin,
        hiddenTabs:newClient.hiddenTabs,
      });
      const url=`${window.location.origin}/join/${cl.invite_token}`;
      const message=`Hi ${titleCaseName(newClient.firstName)},

You've been invited to your private client portal in Integro — your dedicated space to track projects, share files, and work directly with our team.

GAIN ACCESS:
Click the link below to enter your PIN, then verify your details and set your own password.

Link: ${url}
PIN: ${newClient.pin}

Once you're in, everything we're working on together lives here. Please reach out if you have any questions.

— The Integro Team`;
      setInviteResult({url, message});
      setNewClient({firstName:"",lastName:"",company:"",email:"",pin:"",hiddenTabs:[]});
      onCreated&&onCreated(cl);
    }catch(e){alert("Failed to create client: "+e.message);}
    setAddingClient(false);
  }

  function copyUrl(){
    navigator.clipboard.writeText(inviteResult.url);
    setCopied(true);setTimeout(()=>setCopied(false),2000);
  }

  function copyMessage(){
    navigator.clipboard.writeText(inviteResult.message);
    setCopiedMessage(true);setTimeout(()=>setCopiedMessage(false),2000);
  }

  // Hides the post-create success banner and returns the user to the blank
  // form. The input fields are already cleared by handleAddClient on success,
  // so this just drops inviteResult + any in-flight Copied! flashes.
  function handleFinished(){
    setInviteResult(null);
    setCopied(false);
    setCopiedMessage(false);
  }

  return (
    <Card style={{padding:"22px 24px",marginBottom:20}}>
      <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,marginBottom:4}}>Add New Client</div>
      <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:16}}>Create a client profile and generate their unique invite link.</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:14}}>
        {[["firstName","First Name","Jane","text"],["lastName","Last Name","Smith","text"],["company","Company","Acme Co.","text"],["email","Email","jane@company.com","email"]].map(([k,l,ph,t])=>(
          <div key={k}>
            <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>{l}</label>
            <input type={t} value={newClient[k]} onChange={e=>setNewClient(p=>({...p,[k]:e.target.value}))} placeholder={ph}
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
        ))}
        <div>
          <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>5-Digit PIN</label>
          <input value={newClient.pin} onChange={e=>setNewClient(p=>({...p,pin:e.target.value.replace(/\D/g,"").slice(0,5)}))} placeholder="12345" maxLength={5}
            style={{width:"100%",background:C.bgAlt,border:`1px solid ${newClient.pin.length===5?C.green:C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.mono,fontSize:16,letterSpacing:".2em",boxSizing:"border-box"}}/>
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Tab Visibility</label>
        <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:8}}>Choose which tabs this client can see. All visible by default.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {["Projects","Files","Docs","Team Members","Billing","Resources","Timeline","Messages","Sessions"].map(tabName=>{
            const visible=!(newClient.hiddenTabs||[]).includes(tabName);
            return (
              <button key={tabName} type="button" onClick={()=>{
                setNewClient(p=>{
                  const cur=p.hiddenTabs||[];
                  const updated=cur.includes(tabName)?cur.filter(t=>t!==tabName):[...cur,tabName];
                  return {...p,hiddenTabs:updated};
                });
              }} style={{
                background:visible?C.greenBg:C.redBg,
                color:visible?C.green:C.red,
                border:`1px solid ${visible?C.greenBd:C.redBd}`,
                borderRadius:6,padding:"6px 13px",
                fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer",
                display:"inline-flex",alignItems:"center",gap:6,
              }}>
                {visible?"●":"○"} {tabName}
              </button>
            );
          })}
        </div>
        <div style={{fontFamily:F.body,fontSize:10,color:C.charLight,marginTop:8}}>● = visible to client &nbsp;·&nbsp; ○ = hidden from client</div>
      </div>
      <Btn variant="primary" onClick={handleAddClient} loading={addingClient} disabled={!newClient.firstName||newClient.pin.length!==5}>Create Client &amp; Generate Link</Btn>
      {inviteResult&&(
        <div style={{marginTop:16,background:C.greenBg,border:`1px solid ${C.greenBd}`,borderRadius:8,padding:"14px 16px"}}>
          <div style={{fontFamily:F.body,fontSize:12,color:C.green,fontWeight:700,marginBottom:6}}>Client created! Share this invite link:</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <div style={{flex:1,fontFamily:F.mono,fontSize:11,color:C.charcoal,background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px",wordBreak:"break-all"}}>{inviteResult.url}</div>
            <button onClick={copyUrl} style={{background:copied?C.green:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"8px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>
              {copied?"Copied!":"Copy Link"}
            </button>
          </div>
          <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginTop:14,marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Copy-Paste Message</label>
          <textarea readOnly value={inviteResult.message} rows={11}
            style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box",resize:"vertical",marginBottom:10}}/>
          <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between"}}>
            <button onClick={copyMessage}
              style={{background:copiedMessage?C.green:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
              {copiedMessage?"Copied!":"Copy Message"}
            </button>
            <button onClick={handleFinished}
              style={{background:C.surface,color:C.navy,border:`1px solid ${C.navy}`,borderRadius:7,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
              Finished
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
